import React, { useState, useRef, useEffect } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

const ImageCropper = ({ 
  open, 
  onClose, 
  imageSrc, 
  onCropComplete,
  aspectRatio = 1, // Default to square
  recommendedSize = { width: 512, height: 512 },
  title = "Crop Image",
  allowFreeform = false // Allow free-form cropping without fixed aspect ratio
}) => {
  const [crop, setCrop] = useState({
    unit: '%',
    width: 90,
    aspect: allowFreeform ? undefined : aspectRatio,
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);

  // Reset crop when modal opens/closes or image changes
  useEffect(() => {
    if (open) {
      setCrop({
        unit: '%',
        width: 100,
        height: 100,
        x: 0,
        y: 0,
        aspect: allowFreeform ? undefined : aspectRatio,
      });
      // Set initial completed crop to full image so save button is enabled
      setCompletedCrop({
        unit: '%',
        width: 100,
        height: 100,
        x: 0,
        y: 0,
      });
    }
  }, [open, imageSrc, allowFreeform, aspectRatio]);

  const getCroppedImg = (image, crop, outputFormat = 'image/png') => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    const pixelCrop = {
      x: crop.x * scaleX,
      y: crop.y * scaleY,
      width: crop.width * scaleX,
      height: crop.height * scaleY,
    };

    // For freeform, scale to recommended width while maintaining aspect ratio
    // For fixed aspect ratio, use recommended size
    if (allowFreeform) {
      const scale = recommendedSize.width / pixelCrop.width;
      canvas.width = recommendedSize.width;
      canvas.height = Math.round(pixelCrop.height * scale);
    } else {
      canvas.width = recommendedSize.width;
      canvas.height = recommendedSize.height;
    }

    const ctx = canvas.getContext('2d');
    
    // Clear canvas to transparent
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, outputFormat, 0.95);
    });
  };

  const handleSave = async () => {
    if (!completedCrop || !imgRef.current) {
      return;
    }

    try {
      // Detect if original image is PNG to preserve transparency
      const isPNG = imageSrc.includes('data:image/png');
      const format = isPNG ? 'image/png' : 'image/jpeg';
      const croppedImageBlob = await getCroppedImg(imgRef.current, completedCrop, format);
      onCropComplete(croppedImageBlob, format);
      onClose();
    } catch (e) {
      console.error('Error cropping image:', e);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <p className="text-sm text-gray-600">
            {allowFreeform 
              ? `Drag corners to resize • Max width: ${recommendedSize.width}px (height scales automatically)`
              : `Recommended size: ${recommendedSize.width}×${recommendedSize.height}px`
            }
          </p>
        </DialogHeader>

        <div className="max-h-[500px] overflow-auto bg-gray-100 rounded-lg p-4">
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={allowFreeform ? undefined : aspectRatio}
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Crop preview"
              style={{ maxWidth: '100%', maxHeight: '450px' }}
              onLoad={() => {
                // Set initial crop to full image after load
                if (imgRef.current) {
                  const { width, height } = imgRef.current;
                  setCrop({
                    unit: 'px',
                    width: width,
                    height: height,
                    x: 0,
                    y: 0,
                    aspect: allowFreeform ? undefined : aspectRatio,
                  });
                  setCompletedCrop({
                    unit: 'px',
                    width: width,
                    height: height,
                    x: 0,
                    y: 0,
                  });
                }
              }}
            />
          </ReactCrop>
        </div>

        <p className="text-xs text-gray-500 text-center">
          {allowFreeform 
            ? 'Drag corners to select any size • Drag center to reposition'
            : 'Drag corners to resize • Drag center to reposition'
          }
        </p>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!completedCrop}>
            Crop & Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropper;
